import {
  AGENT_PANE_METRICS,
  LIST_PANE_METRICS,
  WORKSPACE_DETAIL_MIN_WIDTH,
  WORKSPACE_RESIZE_HANDLE_WIDTH,
  type ResizablePaneMetrics,
} from "./layoutMetrics";

const FLOATING_POINT_EPSILON = 0.01;

export interface ResolveThreePanelLayoutOptions {
  readonly containerWidth: number;
  readonly listWidth: number;
  readonly sidebarWidth?: number;
  readonly hasSidebar?: boolean;
  readonly detailMinWidth?: number;
  readonly handleWidth?: number;
  readonly listMetrics?: ResizablePaneMetrics;
  readonly sidebarMetrics?: ResizablePaneMetrics;
}

export interface ResolvedThreePanelLayout {
  readonly listWidth: number;
  readonly detailWidth: number;
  readonly sidebarWidth: number;
  readonly minLayoutWidth: number;
  readonly totalWidth: number;
}

export function clampPaneWidth(
  width: number,
  metrics: ResizablePaneMetrics,
): number {
  if (!Number.isFinite(width)) {
    return metrics.defaultWidth;
  }

  return Math.min(metrics.maxWidth, Math.max(metrics.minWidth, width));
}

export function getThreePanelMinLayoutWidth({
  hasSidebar = true,
  detailMinWidth = WORKSPACE_DETAIL_MIN_WIDTH,
  handleWidth = WORKSPACE_RESIZE_HANDLE_WIDTH,
  listMetrics = LIST_PANE_METRICS,
  sidebarMetrics = AGENT_PANE_METRICS,
}: Omit<
  ResolveThreePanelLayoutOptions,
  "containerWidth" | "listWidth" | "sidebarWidth"
> = {}): number {
  const handleCount = hasSidebar ? 2 : 1;

  return (
    listMetrics.minWidth +
    detailMinWidth +
    handleCount * handleWidth +
    (hasSidebar ? sidebarMetrics.minWidth : 0)
  );
}

function shrinkWidthsToFit(
  requestedWidths: readonly number[],
  minima: readonly number[],
  availableWidth: number,
): number[] {
  const resolved = requestedWidths.map((width, index) =>
    Math.max(minima[index] ?? 0, width),
  );

  let overflow =
    resolved.reduce((total, width) => total + width, 0) - availableWidth;

  if (overflow <= FLOATING_POINT_EPSILON) {
    return resolved;
  }

  while (overflow > FLOATING_POINT_EPSILON) {
    const adjustable = resolved
      .map((width, index) => ({
        index,
        slack: width - (minima[index] ?? 0),
      }))
      .filter((entry) => entry.slack > FLOATING_POINT_EPSILON);

    if (adjustable.length === 0) {
      break;
    }

    const totalSlack = adjustable.reduce((total, entry) => total + entry.slack, 0);
    let reducedThisRound = 0;

    for (const entry of adjustable) {
      const currentWidth = resolved[entry.index] ?? minima[entry.index] ?? 0;
      const currentSlack = currentWidth - (minima[entry.index] ?? 0);
      const share =
        totalSlack <= FLOATING_POINT_EPSILON
          ? overflow / adjustable.length
          : overflow * (currentSlack / totalSlack);
      const reduction = Math.min(currentSlack, share);
      resolved[entry.index] = currentWidth - reduction;
      reducedThisRound += reduction;
    }

    if (reducedThisRound <= FLOATING_POINT_EPSILON) {
      break;
    }

    overflow -= reducedThisRound;
  }

  if (overflow > FLOATING_POINT_EPSILON) {
    for (let index = resolved.length - 1; index >= 0; index -= 1) {
      const currentWidth = resolved[index] ?? minima[index] ?? 0;
      const currentSlack = currentWidth - (minima[index] ?? 0);
      if (currentSlack <= FLOATING_POINT_EPSILON) {
        continue;
      }

      const reduction = Math.min(currentSlack, overflow);
      resolved[index] = currentWidth - reduction;
      overflow -= reduction;

      if (overflow <= FLOATING_POINT_EPSILON) {
        break;
      }
    }
  }

  return resolved.map((width, index) => Math.max(minima[index] ?? 0, width));
}

export function resolveThreePanelLayout({
  containerWidth,
  listWidth,
  sidebarWidth,
  hasSidebar = true,
  detailMinWidth = WORKSPACE_DETAIL_MIN_WIDTH,
  handleWidth = WORKSPACE_RESIZE_HANDLE_WIDTH,
  listMetrics = LIST_PANE_METRICS,
  sidebarMetrics = AGENT_PANE_METRICS,
}: ResolveThreePanelLayoutOptions): ResolvedThreePanelLayout {
  const safeContainerWidth = Math.max(0, containerWidth);
  const minLayoutWidth = getThreePanelMinLayoutWidth({
    hasSidebar,
    detailMinWidth,
    handleWidth,
    listMetrics,
    sidebarMetrics,
  });
  const totalWidth = safeContainerWidth;
  const handleCount = hasSidebar ? 2 : 1;
  const constrainedDetailMinWidth = Math.min(
    detailMinWidth,
    Math.max(0, totalWidth - handleCount * handleWidth),
  );
  const availableResizableWidth = Math.max(
    0,
    totalWidth - constrainedDetailMinWidth - handleCount * handleWidth,
  );

  const requestedWidths = [clampPaneWidth(listWidth, listMetrics)];
  // @tested-by: tst_fe_scn_agent_003
  // @invariant: the workspace must fit inside the visible container instead of
  // forcing horizontal scrolling when the viewport is narrower than nominal pane minima.
  const shouldRelaxPaneMinima = safeContainerWidth < minLayoutWidth;
  const minima = [shouldRelaxPaneMinima ? 0 : listMetrics.minWidth];

  if (hasSidebar) {
    requestedWidths.push(clampPaneWidth(sidebarWidth ?? sidebarMetrics.defaultWidth, sidebarMetrics));
    minima.push(shouldRelaxPaneMinima ? 0 : sidebarMetrics.minWidth);
  }

  const resolvedWidths = shrinkWidthsToFit(
    requestedWidths,
    minima,
    availableResizableWidth,
  );
  const resolvedListWidth = resolvedWidths[0] ?? listMetrics.minWidth;
  const resolvedSidebarWidth = resolvedWidths[1] ?? 0;

  const detailWidth =
    Math.max(0, totalWidth -
    handleCount * handleWidth -
    resolvedListWidth -
    resolvedSidebarWidth);

  return {
    listWidth: resolvedListWidth,
    detailWidth,
    sidebarWidth: resolvedSidebarWidth,
    minLayoutWidth,
    totalWidth,
  };
}
